import type { NeighbourhoodPerk } from '@/api/neighbourhood-perks'
import type { SelectOption } from '@/components/ui/select'
import type { SortOrder } from '@/components/ui/table'

import { filterBySearch } from '../../../../shared/person-table.utils'
import { PerkType } from './perk-form-modal.constants'
import { PERK_CATEGORIES, type PerkCategoryKey } from './perks.constants'
import { PerkSortKey } from './perks.sort-keys'

const getSortValue = (perk: NeighbourhoodPerk, sortBy: PerkSortKey) => {
  switch (sortBy) {
    case PerkSortKey.category:
      return perk.category
    case PerkSortKey.description:
      return perk.description
    case PerkSortKey.discount:
      return perk.tag
    case PerkSortKey.name:
      return perk.name
    case PerkSortKey.offer:
      return perk.url ?? perk.link
    case PerkSortKey.type:
      return perk.url ? 'website' : 'promoCode'
    default:
      return ''
  }
}

export const filterPerks = (
  perks: NeighbourhoodPerk[],
  category: string[] | undefined,
  type: PerkType[] | undefined,
) =>
  perks.filter(
    (perk) =>
      (!category?.length || category.includes(perk.category)) &&
      (!type?.length || type.includes(perk.url ? PerkType.url : PerkType.link)),
  )

export const searchPerks = (perks: NeighbourhoodPerk[], search: string | undefined) =>
  filterBySearch(perks, search, (perk) => [
    perk.name,
    perk.description,
    perk.category,
    perk.tag,
    perk.url,
    perk.link,
  ])

export const sortPerks = (
  perks: NeighbourhoodPerk[],
  sortBy: PerkSortKey | null | undefined,
  sortOrder: SortOrder | null | undefined,
) => {
  if (!sortBy || !sortOrder) {
    return [...perks].sort((a, b) => b.id - a.id)
  }

  const direction = sortOrder === 'asc' ? 1 : -1

  return [...perks].sort((a, b) => {
    const aValue = getSortValue(a, sortBy) ?? ''
    const bValue = getSortValue(b, sortBy) ?? ''
    return aValue.localeCompare(bValue, undefined, { sensitivity: 'base' }) * direction
  })
}

export const getPerkCategoryOptions = (
  getLabel: (key: PerkCategoryKey) => string,
): SelectOption<string>[] =>
  PERK_CATEGORIES.map(({ key, text }) => ({ label: getLabel(key), value: text })).sort((a, b) =>
    a.label.localeCompare(b.label),
  )
